import React, { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@material-ui/core/Box'
import Divider from '@material-ui/core/Divider'
import Drawer from '@material-ui/core/Drawer'
import Fade from '@material-ui/core/Fade'
import IconButton from '@material-ui/core/IconButton'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import useTheme from '@material-ui/core/styles/useTheme'
import GcgCircularProgress from '../../Loaders/GcgCircularProgress'
import { makeStyles } from '@material-ui/core/styles'
import MerchantCard from '../../Cards/MerchantCard'
import Typography from '@material-ui/core/Typography'
import DeleteForeverIcon from '@material-ui/icons/DeleteForever'
import TimerIcon from '@material-ui/icons/Timer'
import Alert from '@material-ui/lab/Alert'
import Button from '@material-ui/core/Button'
import CartDrawerHeader from './CartDrawerHeader'
import CartItemQuantityStepper from './CartItemQuantityStepper'
import useFetchCart from '../../../hooks/useFetchCart'
import useUpdateCart from '../../../hooks/useUpdateCart'
import LargeGcgCircularProgress from '../../Loaders/LargeGcgCircularProgress'
import { useQueryClient } from 'react-query'
import {
    formatAmount,
    getCartItemProductGiftDetails,
    getCartItemProductImage,
} from '../../../utility'
import isEmpty from 'lodash/isEmpty'
import EmptyCartBuyCardItem from './EmptyCartBuyCardItem'
import PromoCodeInput from './PromoCodeInput'
import { useQueryParam } from '../../../hooks/useQueryParam'
import useApplyPromoCode from '../../../hooks/useApplyPromoCode'
import useRemovePromoCode from '../../../hooks/useRemovePromoCode'
import useValidateVoucherifyPromoCode from '../../../hooks/useValidateVoucherifyPromoCode'
import {
    addFormErrors,
    removeErrorFromPaths,
} from '../../../utils/formErrorUtils'
import {
    applyVoucherifyPreviewToCart,
    removeVoucherifyPreviewFromCart,
    rememberVoucherifyPromoCode,
    forgetVoucherifyPromoCode,
    readVoucherifyPromoCode,
    markVoucherifyPromoCodeManuallyRemoved,
    hasManuallyRemovedVoucherifyPromoCode,
} from '../../../utils/voucherifyUtils'
import {
    GIFT_LIMIT_EXCEEDED,
    GIFT_RECIPIENT_ORDER_LIMIT_EXCEEDED,
    ORDER_LIMIT_EXCEEDED,
} from '../../../constants'
import { useDispatch, useSelector } from 'react-redux'
import { setCartDrawerOpen } from '../../../actions/cartDrawer'
import { getCloudinaryPublicIdFromWcpProductOrGiftTemplate } from '../../../utils/cloudinaryUtils'
import CardPreview from '../../Cards/CardPreview'
import CartFlyoutPaymentIcons from '../../SmallBusiness/OrderFlow/SmbOrderFlowExpress/CartFlyoutPaymentIcons'

const useStyles = makeStyles((theme) => ({
    cartDrawerOverrides: {
        display: 'flex',
        height: '100%',
        minHeight: '70%',
        width: '97%',
        [theme.breakpoints.up('sm')]: {
            width: '60%',
        },
        maxWidth: 500,
    },
    cartDrawerContainer: {
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        borderRadius: 5,
        fontSize: '0.98rem',
    },
    cartDrawerContainerLoading: {
        background: '#FFFFFF',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 2,
        marginTop: '4.25rem',
        paddingTop: '2rem',
        textAlign: 'center',
    },
    cartHeaderContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
        fontSize: '1.5rem',
        backgroundColor: '#F4F4F4',
        [theme.breakpoints.up('md')]: {
            padding: `${theme.spacing(1)}px ${theme.spacing(3)}px`,
        },
    },
    cartDrawerHeaderCloseButton: {
        padding: '0.6rem',
        color: theme.palette.black,
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    alertMessageContainer: {
        width: '95%',
        marginTop: '1rem',
        alignSelf: 'center',
        padding: '0.25rem 0.75rem',
        [theme.breakpoints.up('md')]: {
            width: '91%',
        },
    },
    emptyCartTextContainer: {
        padding: '10px 5px',
        textAlign: 'center',
    },
    emptyCartText: {
        fontSize: '1.25rem',
    },
    cartButtonContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '1.5rem',
    },
    cartButton: {
        textTransform: 'none',
    },
    checkoutButton: {
        fontSize: '1.3rem',
        margin: '0 0.75rem 1.4rem',
        [theme.breakpoints.up('md')]: {
            margin: '0 1.5rem 1.4rem',
        },
    },
    emptyCartButton: {
        fontSize: '1.1rem',
        maxWidth: 207,
        padding: '1rem',
        marginBottom: '2rem',
        [theme.breakpoints.up('sm')]: {
            marginTop: '1rem',
        },
    },
    cartItemContainer: {
        padding: '0.75rem',
        '&:not(:first-child)': {
            paddingTop: '0.25rem',
        },
        [theme.breakpoints.up('md')]: {
            padding: '1.5rem',
        },
        '&:last-child': {
            paddingBottom: '0.25rem',
        },
    },
    cartItem: {
        alignItems: 'flex-start',
        paddingTop: 0,
        paddingBottom: 10,
        [theme.breakpoints.up('sm')]: {
            columnGap: '.8rem',
        },
    },
    cartItemImage: {
        width: '26%',
        height: 'auto',
        flexGrow: 0,
        borderRadius: 8,
    },
    cartItemDetailsContainer: {
        width: '42%',
        paddingLeft: 5,
        [theme.breakpoints.up('sm')]: {
            paddingLeft: 0,
        },
    },
    cartItemTotalContainer: {
        display: 'flex',
        flexGrow: 1,
        justifyContent: 'flex-end',
    },
    cartItemPriceCashbackContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        [theme.breakpoints.up('md')]: {
            paddingRight: '1rem',
        },
    },
    trashButton: {
        zIndex: 1,
        alignSelf: 'flex-start',
        padding: 0,
        paddingLeft: 3,
        color: theme.palette.black,
        '&:hover': {
            backgroundColor: 'transparent',
        },
        '&:active': {
            backgroundColor: 'transparent',
        },
    },
    cartItemProductName: {
        fontSize: '1.2rem',
        lineHeight: '1em',
    },
    cartItemType: {
        fontSize: '0.825rem',
        padding: '0.5em 0 0.25em',
    },
    cartItemEditLink: {
        padding: '0.5em 0 0.25em',
        '& a': {
            color: '#505E71',
            fontWeight: 'bold',
            fontSize: '14px',
            textDecoration: 'underline',
        },
    },
    cartItemPrice: {
        fontSize: '1rem',
        color: theme.palette.primary.main,
        whiteSpace: 'nowrap',
        paddingLeft: 4,
        [theme.breakpoints.up('sm')]: {
            paddingLeft: 0,
        },
    },
    cartItemCashback: {
        whiteSpace: 'nowrap',
        fontSize: '0.8rem',
    },
    giftDetailsContainer: {
        paddingBottom: '1.25rem',
    },
    giftDetails: {
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.93rem',
    },
    giftDetailsIcon: {
        fontSize: '1rem',
        marginRight: 3,
    },
    cartItemPromotionLabel: {
        fontSize: '1rem',
        backgroundColor: '#01AEA4',
        color: '#FFFFFF',
        borderRadius: 5,
        padding: 10,
        marginTop: 10,
    },
    cartItemDivider: {
        height: 2,
    },
    orderSummaryTotalContainer: {
        marginTop: '-0.4rem',
    },
    orderSummaryTotalSection: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '0.75rem',
        paddingTop: '0.5rem',
        [theme.breakpoints.up('md')]: {
            padding: '1.5rem',
        },
    },
    orderSummaryTotalCashbackSection: {
        paddingBottom: '1.4rem',
    },
    orderSummaryText: {
        fontSize: '1rem',
    },
    orderTotalText: {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        '&:last-child': {
            color: theme.palette.primary.main,
        },
    },
    promoCodeInputContainer: {
        padding: '0 0.75rem',
        [theme.breakpoints.up('md')]: {
            padding: '0 1.5rem',
        },
    },
}))

const CartDrawer = () => {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'), {
        defaultMatches: true,
    })
    const cartDrawerContainerRef = useRef(null)
    const queryClient = useQueryClient()
    const query = useQueryParam()
    const orderWasReordered = query.get('reorder') !== null
    const cartDrawerOpen = useSelector(
        (state) => state.cartDrawer.cartDrawerOpen
    )
    const dispatch = useDispatch()
    const setOpen = useCallback(
        (isOpen) => {
            dispatch(setCartDrawerOpen(isOpen))
        },
        [dispatch]
    )
    const [isAddingToCart, setIsAddingToCart] = useState(false)
    const [errors, setErrors] = useState([])
    const baseUrl = window.React?.base_url
    const voucherifyPromoCodesEnabled =
        window.React?.voucherify_promos_enabled === '1'
    const [voucherifyRemoveIsLoading, setVoucherifyRemoveIsLoading] =
        useState(false)
    const [lastAutoAppliedVoucherifyPromoCode, setLastAutoAppliedVoucherifyPromoCode] =
        useState(null)
    const cartItemsSignatureRef = useRef(null)

    const handleScrollToTop = () => {
        cartDrawerContainerRef.current.scrollIntoView({
            block: 'start',
        })
    }

    const {
        data: cartResponse,
        error: errorGettingCart,
        isLoading: loadingCart,
    } = useFetchCart()

    const {
        mutate: updateCart,
        isLoading: updatingCart,
        isError: errorUpdatingCart,
        errorMessage: errorUpdatingCartMessage,
    } = useUpdateCart()

    const {
        mutateAsync: applyPromoCodeLegacy,
        isLoading: legacyApplyPromoIsLoading,
    } = useApplyPromoCode()

    const {
        mutateAsync: removePromoCodeLegacy,
        isLoading: legacyRemovePromoIsLoading,
    } = useRemovePromoCode()

    const {
        mutateAsync: validateVoucherifyPromoCode,
        isLoading: voucherifyValidateIsLoading,
    } = useValidateVoucherifyPromoCode()

    const applyPromoIsLoading = voucherifyPromoCodesEnabled
        ? voucherifyValidateIsLoading
        : legacyApplyPromoIsLoading

    const removePromoIsLoading = voucherifyPromoCodesEnabled
        ? voucherifyRemoveIsLoading
        : legacyRemovePromoIsLoading

    useEffect(() => {
        if (errorUpdatingCart) {
            setErrors([errorUpdatingCartMessage])
        }
    }, [errorUpdatingCart, errorUpdatingCartMessage])

    useEffect(() => {
        if (errors.length > 0) {
            handleScrollToTop()
        }
    }, [errors])

    useEffect(() => {
        const createCartItemsSignature = (items) =>
            items
                .map((item) => {
                    const identifier =
                        item?.item_id ??
                        item?.cart_item_id ??
                        item?.id ??
                        'unknown'
                    const quantity = item?.quantity ?? item?.qty ?? 0

                    return `${identifier}:${quantity}`
                })
                .join('|')

        if (!voucherifyPromoCodesEnabled) {
            cartItemsSignatureRef.current = null
            return
        }

        const cartItemsSnapshot = cartResponse?.cart?.cart_items

        if (!Array.isArray(cartItemsSnapshot)) {
            cartItemsSignatureRef.current = null
            return
        }

        const currentSignature = createCartItemsSignature(cartItemsSnapshot)

        if (hasManuallyRemovedVoucherifyPromoCode()) {
            cartItemsSignatureRef.current = currentSignature
            return
        }

        if (cartItemsSignatureRef.current !== currentSignature) {
            cartItemsSignatureRef.current = currentSignature
            setLastAutoAppliedVoucherifyPromoCode(null)
        }
    }, [voucherifyPromoCodesEnabled, cartResponse?.cart?.cart_items])

    useEffect(() => {
        if (!voucherifyPromoCodesEnabled) {
            forgetVoucherifyPromoCode()
            setLastAutoAppliedVoucherifyPromoCode(null)
            return
        }

        const cartId = cartResponse?.cart?.cart_id
        if (!cartId || voucherifyRemoveIsLoading) {
            return
        }

        const promoWasManuallyRemoved =
            hasManuallyRemovedVoucherifyPromoCode()

        if (promoWasManuallyRemoved) {
            forgetVoucherifyPromoCode()
            if (lastAutoAppliedVoucherifyPromoCode !== null) {
                setLastAutoAppliedVoucherifyPromoCode(null)
            }
            return
        }

        const storedPromoCode = readVoucherifyPromoCode()

        if (!storedPromoCode) {
            if (lastAutoAppliedVoucherifyPromoCode !== null) {
                setLastAutoAppliedVoucherifyPromoCode(null)
            }
            return
        }

        if (storedPromoCode === lastAutoAppliedVoucherifyPromoCode) {
            return
        }

        const autoApplyVoucher = async () => {
            try {
                const payload = {
                    cart_id: cartId,
                    promo_code: storedPromoCode,
                }

                const voucherifyResponse =
                    await validateVoucherifyPromoCode(payload)

                if (!voucherifyResponse?.valid) {
                    throw new Error('Invalid voucherify response')
                }

                applyVoucherifyPreviewToCart(
                    queryClient,
                    storedPromoCode,
                    voucherifyResponse
                )
                rememberVoucherifyPromoCode(storedPromoCode)
            } catch (error) {
                forgetVoucherifyPromoCode()
            } finally {
                setLastAutoAppliedVoucherifyPromoCode(storedPromoCode)
            }
        }

        autoApplyVoucher()
    }, [
        voucherifyPromoCodesEnabled,
        cartResponse,
        validateVoucherifyPromoCode,
        queryClient,
        lastAutoAppliedVoucherifyPromoCode,
        voucherifyRemoveIsLoading,
    ])

    const {
        cart_items: cartItems,
        cart_id: cartId,
        delivery_groups: deliveryGroups,
        promo_code_redemption: promoCodeRedemption,
        total_price: totalPrice,
        total_customization_fees: totalCustomizationFees,
        total_greeting_card_fees: totalGreetingCardFees,
        total_handwrytten_fees: totalHandwryttenFees,
        total_processing_fees: totalProcessingFees,
        total_shipping_fees: totalShippingFees,
        total_cash_back: totalCashback,
        order_total: orderTotal,
        total_quantity: cartQuantity,
        total_discount: totalDiscount,
        messages,
    } = cartResponse?.cart || {}

    const shippingFeeWithDiscount =
        totalShippingFees - (promoCodeRedemption?.shipping_fee_discount ?? 0)
    const processingFeeWithDiscount =
        totalProcessingFees -
        (promoCodeRedemption?.processing_fee_discount ?? 0)
    const greetingCardFeeWithDiscount =
        totalGreetingCardFees -
        (promoCodeRedemption?.greeting_card_fee_discount ?? 0)
    const handwryttenFeeWithDiscount = totalHandwryttenFees //No discount functionality for Handwrytten yets
    const customizationFeeWithDiscount =
        totalCustomizationFees -
        (promoCodeRedemption?.customization_fee_discount ?? 0)
    const loadValueWithDiscount =
        totalPrice - (promoCodeRedemption?.load_value_discount ?? 0)
    useEffect(() => {
        // CartDrawer button
        const navCartBtn = document.querySelector('[data-activates=cart]')
        if (navCartBtn) {
            navCartBtn.addEventListener('click', function () {
                setOpen(true)
            })
        }

        // Custom Event Listeners
        document.addEventListener('hideCartError', () => {
            hideCartError()
        })
        document.addEventListener(
            'handleAddToCartResponse',
            ({ detail: cartResponse }) => {
                handleAddToCartResponse(cartResponse)
            }
        )
        document.addEventListener('handleCartErrorResponse', (event) => {
            handleCartErrorResponse(event.detail.response)
        })

        window.openCartDrawer = () => {
            setIsAddingToCart(true)
            setOpen(true)
        }

        window.hideCartError = () => {
            setErrors([])
        }

        window.handleAddToCartResponse = (cartResponse) => {
            queryClient.setQueryData('cart', cartResponse)

            if (cartResponse.message) {
                setErrors([cartResponse.message])
            }

            setIsAddingToCart(false)
        }

        window.handleCartErrorResponse = (cartResponse) => {
            let errorMsg = 'Failed to add to cart. Try again in a moment.'
            const { error_code, message } = cartResponse.data || {}
            if (error_code) {
                if (error_code === ORDER_LIMIT_EXCEEDED) {
                    errorMsg = message
                } else if (error_code === GIFT_LIMIT_EXCEEDED) {
                    errorMsg = message
                } else if (error_code === GIFT_RECIPIENT_ORDER_LIMIT_EXCEEDED) {
                    if (
                        cartResponse.data.gift_recipient_order_total_limit &&
                        cartResponse.data
                            .gift_recipient_order_total_limit_window
                    ) {
                        errorMsg =
                            'We are sorry. We are unable to send more than $' +
                            cartResponse.data.gift_recipient_order_total_limit +
                            ' to the same recipient within a ' +
                            cartResponse.data.gift_recipient_order_total_limit_window.replace(
                                '-',
                                ''
                            ) +
                            ' period.'
                    } else {
                        errorMsg =
                            'You have exceeded your order limit for the gift recipient.'
                    }
                }
            }
            setErrors([errorMsg])
            setIsAddingToCart(false)
        }
    }, [queryClient, setOpen])

    useEffect(() => {
        const navCartBtnBadge = document.querySelector('[data-cart-badge]')
        if (navCartBtnBadge) {
            navCartBtnBadge.dataset.cartBadge = cartQuantity
            if (cartQuantity > 0) {
                navCartBtnBadge.classList.remove('no-badge')
                navCartBtnBadge.classList.add('cart-badge')
            } else {
                navCartBtnBadge.classList.remove('cart-badge')
                navCartBtnBadge.classList.add('no-badge')
            }
        }
    }, [cartQuantity, queryClient])

    const removeCartItem = useCallback(
        (cartId, cartItemId) => {
            handleScrollToTop()
            updateCart({
                cart_id: cartId,
                cart_items: [
                    {
                        item_id: cartItemId,
                        quantity: 0,
                    },
                ],
            })
            setErrors([])
            if (orderWasReordered) {
                const urlWithParams = new URL(window.location.href)
                urlWithParams.searchParams.delete('reorder')
                window.location.href = urlWithParams.toString()
            }
        },
        [updateCart, setErrors, orderWasReordered]
    )

    const promoErrorMessage = 'Promo code cannot be applied'
    const removePromoErrorMessage =
        'Unable to remove promo code, refresh and try again'

    const handleApplyPromoCode = async (promoCode) => {
        const payload = {
            cart_id: cartId,
            promo_code: promoCode,
        }

        removeErrorFromPaths(['applyPromoCode'], errors, setErrors)

        try {
            if (voucherifyPromoCodesEnabled) {
                const voucherifyResponse = await validateVoucherifyPromoCode(
                    payload
                )

                if (!voucherifyResponse?.valid) {
                    const voucherifyMessage =
                        voucherifyResponse?.error_message ?? promoErrorMessage

                    throw new Error(voucherifyMessage)
                }

                applyVoucherifyPreviewToCart(
                    queryClient,
                    promoCode,
                    voucherifyResponse
                )
                rememberVoucherifyPromoCode(promoCode)
                setLastAutoAppliedVoucherifyPromoCode(promoCode)
            } else {
                const response = await applyPromoCodeLegacy(payload)
                queryClient.setQueryData('cart', response)
                forgetVoucherifyPromoCode()
                setLastAutoAppliedVoucherifyPromoCode(null)
            }
        } catch (error) {
            console.error('Failed to apply promo code', error)
            forgetVoucherifyPromoCode()
            setLastAutoAppliedVoucherifyPromoCode(null)
            const displayMessage =
                typeof error?.message === 'string' && error.message.trim()
                    ? error.message
                    : promoErrorMessage
            addFormErrors(
                displayMessage,
                ['applyPromoCode'],
                errors,
                setErrors
            )
        }
    }

    const handleRemovePromoCode = async () => {
        const payload = {
            cart_id: cartId,
        }

        removeErrorFromPaths(['removePromoCode'], errors, setErrors)

        if (voucherifyPromoCodesEnabled) {
            setVoucherifyRemoveIsLoading(true)
            try {
                removeVoucherifyPreviewFromCart(queryClient)
                forgetVoucherifyPromoCode()
                markVoucherifyPromoCodeManuallyRemoved()
                setLastAutoAppliedVoucherifyPromoCode(null)
                await queryClient.invalidateQueries('cart')
            } catch (_error) {
                addFormErrors(
                    removePromoErrorMessage,
                    ['removePromoCode'],
                    errors,
                    setErrors
                )
            } finally {
                setVoucherifyRemoveIsLoading(false)
            }

            return
        }

        removePromoCodeLegacy(payload)
            .then((response) => {
                queryClient.setQueryData('cart', response)
                forgetVoucherifyPromoCode()
                setLastAutoAppliedVoucherifyPromoCode(null)
            })
            .catch(() => {
                addFormErrors(
                    removePromoErrorMessage,
                    ['removePromoCode'],
                    errors,
                    setErrors
                )
            })
    }

    useEffect(() => {
        if (orderWasReordered) {
            setOpen(true)
        }
    }, [orderWasReordered, queryClient, setOpen])

    if (loadingCart) {
        return <GcgCircularProgress />
    }    

    const checkoutHref = `${baseUrl}/cart/`

    return (
        <Drawer
            id="cart-drawer"
            classes={{ paper: classes.cartDrawerOverrides }}
            anchor="right"
            open={cartDrawerOpen}
            onClose={() => setOpen(false)}
        >
            <Box
                className={classes.cartDrawerContainer}
                ref={cartDrawerContainerRef}
            >
                <CartDrawerHeader
                    classes={classes}
                    cartQuantity={cartQuantity}
                    closeCartDrawer={() => setOpen(false)}
                />
                {(updatingCart || isAddingToCart) && (
                    <div className={classes.cartDrawerContainerLoading}>
                        <LargeGcgCircularProgress />
                    </div>
                )}
                {messages && messages.length > 0 && (
                    <Alert
                        severity="warning"
                        className={classes.alertMessageContainer}
                        data-testid="cart-message-alert"
                    >
                        {messages.map((message, index) => {
                            return (
                                <React.Fragment key={index}>
                                    {message}
                                </React.Fragment>
                            )
                        })}
                    </Alert>
                )}
                {errors.length > 0 && (
                    <Alert
                        severity="error"
                        className={classes.alertMessageContainer}
                        data-testid="cart-error-alert"
                    >
                        {errors.map((error, index) => {
                            return (
                                <React.Fragment key={index}>
                                    {error} If you keep getting this error
                                    please feel free to contact customer
                                    support.
                                </React.Fragment>
                            )
                        })}
                    </Alert>
                )}
                {errorGettingCart && (
                    <Alert
                        severity="error"
                        className={classes.alertMessageContainer}
                        data-testid="cart-error-getting-cart-alert"
                    >
                        There was a problem getting your cart. Please refresh
                        the page.
                    </Alert>
                )}
                {!cartResponse || isEmpty(cartResponse.cart?.cart_items) ? (
                    <>
                        <Box className={classes.emptyCartTextContainer}>
                            <Typography
                                variant="body1"
                                className={classes.emptyCartText}
                            >
                                Your cart is empty.
                            </Typography>
                            <Typography
                                variant="body1"
                                className={classes.emptyCartText}
                            >
                                Shop these new gift cards and get a lifetime
                                guarantee!
                            </Typography>
                        </Box>
                        <Box className={classes.cartButtonContainer}>
                            <Button
                                className={`${classes.cartButton} ${classes.emptyCartButton}`}
                                href={`${baseUrl}/buy-gift-cards/`}
                                aria-label="Shop new gift cards"
                                variant="contained"
                                fullWidth
                                color="primary"
                                size="large"
                            >
                                Shop New Gift Cards
                            </Button>
                        </Box>
                        <EmptyCartBuyCardItem
                            pageUrl={`${baseUrl}/build-a-card/`}
                            imageUrl="https://res.cloudinary.com/gift-card-granny/image/upload/f_auto/GCG/assets/holidays/summer/visa_bac_2025_ojsbkv"
                            imageTitle="Build a card"
                            title="Create a Custom Visa Gift Card"
                            subtitle="Upload your own photo and message to 
                                        build the perfect gift of a custom Visa
                                        gift card."
                            buttonText="Build-a-Card Now"
                        />
                        <EmptyCartBuyCardItem
                            pageUrl={`${baseUrl}/visa-virtual-gift-card/`}
                            imageUrl="https://marketingcdn.giftcardgranny.com/merchant-images/lg/virtual_visa.png"
                            imageTitle="Visa Virtual card"
                            title="Visa Virtual eGift Cards"
                            subtitle="Instantly send a personalized eGift that can be used online everywhere* Visa is accepted."
                            buttonText="Send an instant eGift"
                        />
                    </>
                ) : (
                    <>
                        <List disablePadding>
                            {cartItems.map((cartItem, index) => {
                                const {
                                    delivery_group_uuid: deliveryGroupUuid,
                                    item_id: cartItemId,
                                    plastic,
                                    product_name: productName,
                                    price: cardPrice,
                                    cash_back: cardCashBack,
                                    delivery_estimate: deliveryEstimate,
                                    open_loop: isOpenLoop,
                                    custom_card_details: customCardDetails,
                                    promotional_offer: promotionalOffer,
                                    gift: isGift,
                                    quantity: cartItemQuantity,
                                    greeting_card: greetingCard,
                                    item_edit_url: itemEditUrl,
                                } = cartItem
                                const cartItemDeliveryGroup =
                                    deliveryGroups.find(
                                        (d) => d.uuid === deliveryGroupUuid
                                    )
                                const productImageUrl =
                                    getCartItemProductImage(cartItem)
                                const cloudinaryId =
                                    customCardDetails?.wcp_product
                                        ? getCloudinaryPublicIdFromWcpProductOrGiftTemplate(
                                              customCardDetails?.wcp_product
                                          )
                                        : null
                                const cardNetworkName =
                                    customCardDetails?.wcp_product?.card_network
                                const emboss1 =
                                    customCardDetails?.recipient_name ?? ''
                                const emboss2 =
                                    customCardDetails?.emboss_message ?? ''

                                const formattedProductName = productName
                                    .replace(/virtual\s/i, '')
                                    .replace( /eGift/gi, isOpenLoop ? 'Gift' : 'Digital Gift')

                                return (
                                    <div
                                        key={cartItemId}
                                        className={`${classes.cartItemContainer}`}
                                    >
                                        <ListItem
                                            className={classes.cartItem}
                                            disableGutters
                                        >
                                            <Fade in timeout={300}>
                                                {cloudinaryId ? (
                                                    <CardPreview
                                                        cloudinaryId={
                                                            cloudinaryId
                                                        }
                                                        cardNetworkName={
                                                            cardNetworkName
                                                        }
                                                        hideOverlay={false}
                                                        imageClassName={
                                                            classes.cartItemImage
                                                        }
                                                        emboss1={emboss1}
                                                        emboss2={emboss2}
                                                    />
                                                ) : (
                                                    <MerchantCard
                                                        imageSrc={
                                                            productImageUrl
                                                        }
                                                        title={
                                                            isOpenLoop
                                                                ? `${customCardDetails?.wcp_product?.name} Gift Card`
                                                                : formattedProductName
                                                        }
                                                        additionalStyles={
                                                            classes.cartItemImage
                                                        }
                                                        lazyLoad={true}
                                                    />
                                                )}
                                            </Fade>
                                            <Box
                                                className={
                                                    classes.cartItemDetailsContainer
                                                }
                                            >
                                                <Typography
                                                    className={
                                                        classes.cartItemProductName
                                                    }
                                                    data-testid={`cart-item-product-name-${cartItemId}`}
                                                >
                                                    {formattedProductName}
                                                </Typography>
                                                {!isEmpty(greetingCard) && (
                                                    <Typography
                                                        className={
                                                            classes.cartItemType
                                                        }
                                                    >
                                                        With a greeting card
                                                    </Typography>
                                                )}
                                                {itemEditUrl && (
                                                    <Typography
                                                        className={
                                                            classes.cartItemEditLink
                                                        }
                                                    >
                                                        <a href={itemEditUrl}>
                                                            Edit
                                                        </a>
                                                    </Typography>
                                                )}
                                                {!isGift &&
                                                    !plastic &&
                                                    !customCardDetails && (
                                                        <CartItemQuantityStepper
                                                            cartId={cartId}
                                                            cartItemId={
                                                                cartItemId
                                                            }
                                                            quantity={
                                                                cartItemQuantity
                                                            }
                                                            updateCart={
                                                                updateCart
                                                            }
                                                            setErrors={
                                                                setErrors
                                                            }
                                                        />
                                                    )}
                                            </Box>
                                            <Box
                                                className={
                                                    classes.cartItemTotalContainer
                                                }
                                            >
                                                <Box
                                                    className={
                                                        classes.cartItemPriceCashbackContainer
                                                    }
                                                >
                                                    <Typography
                                                        className={
                                                            classes.cartItemPrice
                                                        }
                                                        data-testid={`cart-item-${index}-price`}
                                                    >
                                                        $
                                                        {formatAmount(
                                                            cardPrice
                                                        )}{' '}
                                                        {cartItemQuantity >
                                                            1 && <>ea.</>}
                                                    </Typography>
                                                    {cardCashBack > 0 && (
                                                        <Typography
                                                            className={
                                                                classes.cartItemCashback
                                                            }
                                                        >
                                                            $
                                                            {cartItemQuantity >
                                                            1 ? (
                                                                <>
                                                                    {formatAmount(
                                                                        cardCashBack /
                                                                            cartItemQuantity
                                                                    )}{' '}
                                                                    cash back
                                                                    ea.
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {formatAmount(
                                                                        cardCashBack
                                                                    )}{' '}
                                                                    cash back
                                                                </>
                                                            )}
                                                        </Typography>
                                                    )}
                                                </Box>

                                                <IconButton
                                                    className={`${classes.trashButton}`}
                                                    aria-label="Delete cart item"
                                                    onClick={() =>
                                                        removeCartItem(
                                                            cartId,
                                                            cartItemId
                                                        )
                                                    }
                                                    disableRipple
                                                >
                                                    <DeleteForeverIcon
                                                        style={{
                                                            zIndex: -1,
                                                        }}
                                                    />
                                                </IconButton>
                                            </Box>
                                        </ListItem>
                                        <div
                                            className={
                                                classes.giftDetailsContainer
                                            }
                                        >
                                            <Typography
                                                className={classes.giftDetails}
                                                data-testid={`cart-item-${index}-gift-details`}
                                            >
                                                To:{' '}
                                                {getCartItemProductGiftDetails(
                                                    cartItem,
                                                    cartItemDeliveryGroup
                                                )}
                                            </Typography>
                                            <Typography
                                                className={classes.giftDetails}
                                            >
                                                {!isMobile && (
                                                    <TimerIcon
                                                        className={
                                                            classes.giftDetailsIcon
                                                        }
                                                    />
                                                )}
                                                {cartItemDeliveryGroup &&
                                                plastic
                                                    ? cartItemDeliveryGroup
                                                          .shipping_method
                                                          .delivery_estimate
                                                    : deliveryEstimate}
                                            </Typography>
                                            {promotionalOffer && (
                                                <Box
                                                    className={
                                                        classes.cartItemPromotionLabel
                                                    }
                                                >
                                                    <span
                                                        style={{
                                                            display: 'inline',
                                                        }}
                                                    >
                                                        {promotionalOffer.label}
                                                    </span>
                                                </Box>
                                            )}
                                        </div>
                                        <Divider
                                            className={classes.cartItemDivider}
                                        />
                                    </div>
                                )
                            })}
                        </List>
                        <Box className={classes.orderSummaryTotalContainer}>
                            <Box className={classes.orderSummaryTotalSection}>
                                <Box>
                                    {totalDiscount > 0 && (
                                        <Typography
                                            className={classes.orderSummaryText}
                                        >
                                            Promo Discount
                                        </Typography>
                                    )}
                                    <Typography
                                        className={classes.orderSummaryText}
                                    >
                                        {promoCodeRedemption &&
                                        promoCodeRedemption.load_value_discount >
                                            0
                                            ? 'Gift Card Value Paid'
                                            : 'Gift Card Value'}
                                    </Typography>
                                    <Typography
                                        className={classes.orderSummaryText}
                                    >
                                        Shipping
                                    </Typography>
                                    <Typography
                                        className={classes.orderSummaryText}
                                    >
                                        Processing Fees
                                    </Typography>
                                    {totalCustomizationFees > 0 && (
                                        <Typography
                                            className={classes.orderSummaryText}
                                        >
                                            Personalization Fees
                                        </Typography>
                                    )}
                                    {totalHandwryttenFees > 0 && (
                                        <Typography
                                            className={classes.orderSummaryText}
                                        >
                                            Handwritten Fees
                                        </Typography>
                                    )}
                                    {totalGreetingCardFees > 0 && (
                                        <Typography
                                            className={classes.orderSummaryText}
                                        >
                                            Greeting Card Fees
                                        </Typography>
                                    )}
                                </Box>
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="flex-end"
                                >
                                    {totalDiscount > 0 && (
                                        <Typography
                                            className={classes.orderSummaryText}
                                        >
                                            -$
                                            {formatAmount(totalDiscount)}
                                        </Typography>
                                    )}
                                    <Typography
                                        className={classes.orderSummaryText}
                                    >
                                        ${formatAmount(loadValueWithDiscount)}
                                    </Typography>
                                    <Typography
                                        className={classes.orderSummaryText}
                                        data-testid="shipping-total"
                                    >
                                        ${formatAmount(shippingFeeWithDiscount)}
                                    </Typography>
                                    <Typography
                                        className={classes.orderSummaryText}
                                    >
                                        $
                                        {formatAmount(
                                            processingFeeWithDiscount
                                        )}
                                    </Typography>
                                    {totalCustomizationFees > 0 && (
                                        <Typography
                                            className={classes.orderSummaryText}
                                        >
                                            $
                                            {formatAmount(
                                                customizationFeeWithDiscount
                                            )}
                                        </Typography>
                                    )}
                                    {totalHandwryttenFees > 0 && (
                                        <Typography
                                            className={classes.orderSummaryText}
                                        >
                                            $
                                            {formatAmount(
                                                handwryttenFeeWithDiscount
                                            )}
                                        </Typography>
                                    )}
                                    {totalGreetingCardFees > 0 && (
                                        <Typography
                                            className={classes.orderSummaryText}
                                        >
                                            $
                                            {formatAmount(
                                                greetingCardFeeWithDiscount
                                            )}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            <Box
                                className={classes.orderSummaryTotalSection}
                                flexDirection="column"
                                style={{ paddingTop: 0 }}
                            >
                                <Divider className={classes.cartItemDivider} />
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    flexDirection="row"
                                    style={{ margin: '1rem 0' }}
                                >
                                    <Typography
                                        className={classes.orderTotalText}
                                    >
                                        Order Total
                                    </Typography>
                                    <Typography
                                        className={classes.orderTotalText}
                                        data-testid="order-total"
                                    >
                                        ${formatAmount(orderTotal)}
                                    </Typography>
                                </Box>
                                <Divider className={classes.cartItemDivider} />
                            </Box>
                            {totalCashback > 0 && (
                                <Box
                                    className={`${classes.orderSummaryTotalSection} ${classes.orderSummaryTotalCashbackSection}`}
                                    display="flex"
                                    justifyContent="space-between"
                                    style={{
                                        paddingTop: 0,
                                    }}
                                >
                                    <Typography
                                        className={classes.orderSummaryText}
                                    >
                                        You Earn
                                    </Typography>
                                    <Typography
                                        className={classes.orderSummaryText}
                                    >
                                        ${formatAmount(totalCashback)} cash back
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <Box className={classes.promoCodeInputContainer}>
                            <PromoCodeInput
                                appliedPromoCode={
                                    promoCodeRedemption?.redemption_code
                                }
                                appliedPromoCodeTerms={
                                    promoCodeRedemption?.terms
                                }
                                hasQualifiedItems={
                                    promoCodeRedemption?.has_qualified_items
                                }
                                applyPromoIsLoading={applyPromoIsLoading}
                                removePromoIsLoading={removePromoIsLoading}
                                errors={errors}
                                handleApplyPromoCode={handleApplyPromoCode}
                                handleRemovePromoCode={handleRemovePromoCode}
                            />
                        </Box>
                        <Box className={classes.cartButtonContainer}>
                            <Button
                                className={`${classes.cartButton} ${classes.checkoutButton}`}
                                href={checkoutHref}
                                aria-label="Proceed to checkout"
                                variant="contained"
                                fullWidth
                                color="primary"
                                size="large"
                                data-testid="checkout-button"
                                disabled={
                                    applyPromoIsLoading || removePromoIsLoading
                                }
                            >
                                {applyPromoIsLoading
                                    ? 'Applying promo code...'
                                    : removePromoIsLoading
                                    ? 'Removing promo code...'
                                    : 'Proceed to checkout'}
                            </Button>
                        </Box>
                        <CartFlyoutPaymentIcons />
                    </>
                )}
            </Box>
        </Drawer>
    )
}

export default CartDrawer
